import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Collapse,
  Navbar,
  NavbarToggler,
  NavbarBrand,
  Nav,
  NavItem,
  UncontrolledTooltip,
  Badge
} from 'reactstrap'
import { NavLink, useNavigate } from 'react-router-dom'
import s from './Header.scss'
import { URLS } from 'utils/constants'
import { FaSearch, FaBell, FaPowerOff, FaInfoCircle } from 'react-icons/fa'
import { ProfilePhoto, OutsideClickContainer } from 'components/ideal-core-ui'
import { NotificationsContainer } from 'components/Layout/Header/Notifications'
import { Settings } from 'components/Layout/Header/Settings'
import { Search } from './Search'
import * as ACT from './actions'
import { HelpCenterModal } from 'components/common/modals/HelpCenterModal'
import {
  getDialogVisibleState,
  setDialogVisibleState
} from 'components/common/modals/HelpCenterModal/helpers'
import {
  logout,
  isAdmin,
  getUser,
  isOnlyTimetracker,
  isTimetrackerRegular,
  isTimetrackerAdmin
} from 'utils/authService'
import cn from 'classnames'
import { getModules } from 'utils/paymentService'

const HeaderContainer = ({ className }) => {
  const dispatch = useDispatch()
  const { userImage, logo, backgroundColor, isFetchingLogo } = useSelector(state => state.header)
  const [isOpen, setIsOpen] = useState(false)
  const [unreadNotifications, setUnreadNotifications] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const navigate = useNavigate()
  const [isHelpCenterOpen, setIsHelpCenterOpen] = useState(false)
  const { leads } = getModules()

  const isRestricted = !isOnlyTimetracker()

  const updateUnreadNotifications = unreadNotifications =>
    setUnreadNotifications(unreadNotifications)

  const toggle = () => setIsOpen(!isOpen)

  const setSearching = value => {
    setIsSearching(value)
  }

  useEffect(() => {
    dispatch({ type: ACT.GET_LOGGED_IN_USER_FETCH_REQUESTED })
    dispatch({ type: ACT.GET_LOGO_FETCH_REQUESTED })
  }, [dispatch])

  useEffect(() => {
    setIsHelpCenterOpen(getDialogVisibleState())
  }, [])

  const dialogClose = () => {
    setIsHelpCenterOpen(false)
    setDialogVisibleState(false)
  }

  return (
    <div
      className={cn(s.headerWrapper, {
        [s.isCollapsed]: isOpen,
        [className]: className
      })}
    >
      {isRestricted && (
        <>
          <NotificationsContainer
            updateUnreadNotifications={updateUnreadNotifications}
            notificationTarget="notificationContainer"
          />
          <Settings settingsTarget="settingsContainer" />
        </>
      )}
      <OutsideClickContainer styles={{ height: 'initial' }} closeComponent={() => setIsOpen(false)}>
        <Navbar className={s.header} light expand="xxl">
          <div
            className={s.logoWrapper}
            style={
              logo
                ? {
                    backgroundColor
                  }
                : { ...(isFetchingLogo ? { backgroundColor: 'white' } : {}) }
            }
          >
            <NavbarBrand
              className={s.logo}
              href={URLS.AUTH_HOME}
              style={
                logo
                  ? {
                      backgroundImage: `url(${logo})`,
                      backgroundColor
                    }
                  : { ...(isFetchingLogo ? { visibility: 'hidden' } : { visibility: 'initial' }) }
              }
            />
          </div>
          <NavbarToggler className={s.toggler} onClick={toggle} />
          <Collapse className={s.collapse} isOpen={isOpen} navbar>
            <Nav className={s.nav} navbar>
              {isRestricted && (
                <>
                  <NavItem className={s.link}>
                    <NavLink end to={URLS.AUTH_HOME} onClick={toggle}>
                      Home
                    </NavLink>
                  </NavItem>
                  <NavItem className={s.link}>
                    <NavLink end to={URLS.AUTH_DASHBOARD} onClick={toggle}>
                      Dashboard
                    </NavLink>
                  </NavItem>
                  {leads && (
                    <NavItem className={s.link}>
                      <NavLink to={URLS.AUTH_LEADS} onClick={toggle}>
                        <span>Leads</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem className={s.link}>
                    <NavLink
                      end
                      to={`${URLS.AUTH_DEALS}${URLS.AUTH_DEALS_BOARD}`}
                      onClick={toggle}
                      className={s.opportunities}
                    >
                      <span>Opportunities</span>
                    </NavLink>
                  </NavItem>
                  <NavItem className={s.link}>
                    <NavLink end to={URLS.AUTH_CALENDAR} className={s.calendar} onClick={toggle}>
                      <span>Calendar</span>
                    </NavLink>
                  </NavItem>
                  <NavItem className={s.link}>
                    <NavLink to={URLS.AUTH_CONTACTS} onClick={toggle}>
                      Contacts
                    </NavLink>
                  </NavItem>
                  <NavItem className={s.link}>
                    <NavLink to={URLS.AUTH_COMPANIES} onClick={toggle}>
                      Companies
                    </NavLink>
                  </NavItem>
                </>
              )}
              {(isTimetrackerAdmin() || isTimetrackerRegular()) && (
                <NavItem className={s.link}>
                  <NavLink to={URLS.AUTH_TIME_TRACKING} className={s.time} onClick={toggle}>
                    <span>Time Tracker</span>
                  </NavLink>
                </NavItem>
              )}
              {isRestricted && (
                <>
                  {/* <NavItem className={s.link}>
                  <NavLink end to={URLS.AUTH_LISTS} onClick={toggle}>
                    Lists
                  </NavLink>
                </NavItem> */}
                  {isAdmin() && (
                    <NavItem className={s.link}>
                      <NavLink to={URLS.AUTH_REPORTS} className={s.reports} onClick={toggle}>
                        <span>Reports</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem className={s.link}>
                    <NavLink end to={URLS.AUTH_GLOBAL_SEARCH} onClick={toggle}>
                      <span>Search</span>
                    </NavLink>
                  </NavItem>
                </>
              )}
            </Nav>
          </Collapse>
          <div className={s.headerActionsWrapper}>
            {isRestricted && (
              <>
                <div className={s.searchContainer}>
                  <FaInfoCircle id="search" className={s.searchTooltip} />
                  <UncontrolledTooltip placement="top" target="search">
                    Type q: to search for a quote number, and p: to search for a project number.
                  </UncontrolledTooltip>
                  {!isSearching ? (
                    <FaSearch className={s.searchIcon} />
                  ) : (
                    <span className={s.loadingGif} />
                  )}
                  <Search id="header" setIsSearching={setSearching} count="5" />
                </div>
                <div id="notificationContainer" className={s.notificationContainer}>
                  {unreadNotifications > 0 && (
                    <Badge className={s.badgeNotification} color="success" pill>
                      {unreadNotifications}
                    </Badge>
                  )}
                  <FaBell className={s.notificationIcon} />
                </div>
                <div id="settingsContainer" className={s.photoContainer}>
                  <ProfilePhoto src={userImage} />
                </div>
              </>
            )}
            <div
              id="logout"
              className={cn(s.logoutBtn, { [s.timetracker]: isOnlyTimetracker() })}
              onClick={async () => {
                await logout()
                navigate(URLS.LOGIN)
              }}
            >
              <FaPowerOff />
            </div>
          </div>
          {isRestricted && (
            <UncontrolledTooltip placement="bottom" target="settingsContainer">
              {getUser()?.name}
            </UncontrolledTooltip>
          )}
          <UncontrolledTooltip placement="bottom" target="logout">
            Logout
          </UncontrolledTooltip>
        </Navbar>
      </OutsideClickContainer>
      {isHelpCenterOpen && <HelpCenterModal isOpen={isHelpCenterOpen} dialogClose={dialogClose} />}
    </div>
  )
}

export default HeaderContainer
